/* museum-atlas.js — the field atlas panel and the film player, lifted out of
 * the 10,658-line three.js museum.js for the 2D rebuild.
 *
 * This is PART C of the old file (9828-10658) plus the film signal
 * (9553-9722), ported as written. The panel's job has not changed: the list
 * says what is on the map, the map says where the list's entries were taken,
 * and the two light each other in both directions. What has gone is every
 * line that spoke to a camera. There is no THREE here, no renderer, no
 * projection offset and no per-frame loop; the chart is moved through the
 * `chart` facade handed in at mount, which museum.js implements over
 * FORUM_CHART.
 *
 * Content comes from window.MUSEUM_DATA (museum-data.js) and nowhere else.
 * The two theme settings the film player needs — film_url and youtube_id —
 * still arrive as window.MUSEUM_FILM_URL / window.MUSEUM_YT_ID, written by
 * the section's inline manifest, because they are settings rather than
 * catalogue.
 *
 *   FORUM_ATLAS.mount({ data, chart, onEnterStop })
 *   FORUM_ATLAS.open(regionIndex | null)   null = unscoped, "All regions"
 *   FORUM_ATLAS.close()
 *   FORUM_ATLAS.isOpen()
 *   FORUM_ATLAS.refresh()
 *   FORUM_ATLAS.linkCity(cityKey | [cityKeys] | null)   a pin or marker was hovered
 *   FORUM_ATLAS.cardHoverCity()            the city under the pointer, or null
 *   FORUM_ATLAS.clearRegion()              let go of the region, keep the panel
 *   FORUM_ATLAS.region()
 *   FORUM_ATLAS.playEpisode(season, index)  a DWS season's episode (K22)
 *
 * Deliberate departures from the 3D original, and only these:
 *   1. no 3D calls — mapMoveCamera / mapMoveToRegionView / orbitToStop /
 *      mapFrameStop / camera.setViewOffset / atlasApplyShift / atlasShift all
 *      become facade calls;
 *   2. atlasSyncHotspots becomes chart.setSubs + chart.setRegionSheet, same
 *      logic;
 *   3. Escape closes the panel (it did nothing before), except while the film
 *      or The Hang is up;
 *   4. the region chip carries the live region index, not a hardcoded 1;
 *   5. atlasFitNav's measured 0 is kept on purpose, with the reason written
 *      down;
 *   6. buildHotspotControls and the rest of the dead tune panel are gone.
 */
(function () {
  'use strict';

  /* shopify:section:load runs the section's scripts again on every theme
     editor edit. One panel, one film player, one set of listeners. */
  if (window.FORUM_ATLAS) return;

  /* ---- what we were handed ---------------------------------------------- */
  var D = null;                 /* MUSEUM_DATA */
  var chart = null;             /* the chart facade, normalised below */
  var onEnterStop = null;       /* what a photo card opens instead of the room */
  var onSeason = null;          /* what a DWS season's card opens: the host's season view */
  var mounted = false;

  /* museum.js owns the status line. It is on window by the time we run, but
     call it as if it might not be: a missing status line is not a reason for
     a card not to open. */
  function loadNote(key, text) {
    var api = window.FORUM_MUSEUM || {};
    if (api.loadNote) api.loadNote(key, text);
  }
  function loadNoteDone(key) {
    var api = window.FORUM_MUSEUM || {};
    if (api.loadNoteDone) api.loadNoteDone(key);
  }

  /* Every facade method is optional. A partial facade should leave the panel
     working and the chart still — never throw out of a click handler. */
  function makeFacade(c) {
    var src = c || {};
    var out = {};
    ['focusRegion', 'home', 'zoomHome', 'setSubs', 'setRegionSheet',
     'highlightStation', 'highlightSub', 'setPanelWidth', 'setPeek',
     'showStations'
    ].forEach(function (name) {
      out[name] = function () {
        if (typeof src[name] !== 'function') return undefined;
        return src[name].apply(src, arguments);
      };
    });
    return out;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* One label, one place: "1 entry" reads wrong as "1 entries", and the panel
     and the stepper both say it. */
  function entriesLabel(n) {
    if (D && D.entriesLabel) return D.entriesLabel(n);
    return n + (n === 1 ? ' entry' : ' entries');
  }

  function byId(id) { return document.getElementById(id); }

  /* ======================================================================
     THE FILM SIGNAL
     Where a film entry goes instead of the museum. The file used to sit next
     to this one; it is a theme setting now — the section writes it into
     window.MUSEUM_FILM_URL — so pointing it at Shopify Files or a CDN is
     still one value and nothing else changes.
     ====================================================================== */
  var FILM_SRC = '';
  var YT_ID = '';
  var KIND_VIDEO = {};

  /* Built at mount rather than at script evaluation, which is the one change
     here: the manifest that writes these globals is inline in the section and
     a module that is mounted by hand should read them when it is asked to,
     not when it happens to be parsed. */
  function filmBuildSources() {
    FILM_SRC = String(window.MUSEUM_FILM_URL || '').trim();
    YT_ID = String(window.MUSEUM_YT_ID || '').trim() || 'Bdepz6BX6D4';
    /* What each kind plays. A file sits next to this page and is streamed by
       the browser; an embed is somebody else's player in an iframe. Both open
       the same room. An entry can carry its own `video` to override its kind.
       With no film uploaded there is no file to stream, so a film entry says
       so in the player's own refusal panel rather than pointing a <video>
       at an empty string. */
    KIND_VIDEO = {
      film: FILM_SRC ? { type: 'file', src: FILM_SRC } : { type: 'soon' },
      /* The id rather than an embed URL, because the player is built by
         YouTube's API and it wants the id. `watch` is where to send anyone
         the embed turns away. Deliberately youtube.com and not
         youtube-nocookie.com: the no-cookie host is the one blockers and
         strict privacy modes stop first, and a player that silently does not
         appear is worse than a cookie. */
      society: { type: 'embed', id: YT_ID,
                 watch: 'https://www.youtube.com/watch?v=' + YT_ID }
    };
  }

  function videoFor(media) {
    return (media && media.video) || KIND_VIDEO[media && media.kind] || null;
  }

  var filmView = null, fmVideo = null;
  var fmBlockedNote = null, FM_BLOCKED_REFUSED = '';

  /* ---- YouTube's player -------------------------------------------------
     Loaded the first time an embed is opened and not before: it is a script
     from another origin, and a page that never plays one should never fetch
     it.

     The API is used rather than a plain iframe for one reason: it reports
     failure. An iframe pointed at a video whose owner has disabled embedding
     still fires `load` — it has loaded, it is just showing "video
     unavailable" — so there is no way from outside to tell a working player
     from a refused one. onError says so, and then there is something useful
     to do about it. */
  var ytState = null;            /* null | 'loading' | 'ready' | 'failed' */
  var ytPlayer = null;

  function ytEnsureApi(done) {
    var called = false;
    function finish(ok) { if (!called) { called = true; done(ok); } }
    if (window.YT && window.YT.Player) { ytState = 'ready'; finish(true); return; }
    if (ytState === 'failed') { finish(false); return; }

    /* The API calls this global when it is ready; chained rather than
       replaced in case anything else ever wants it too. */
    var prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prev === 'function') prev();
      ytState = 'ready';
      finish(true);
    };
    if (ytState !== 'loading') {
      ytState = 'loading';
      var tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.onerror = function () { ytState = 'failed'; finish(false); };
      document.head.appendChild(tag);
    }
    /* Blocked at the network rather than refused outright — an extension, an
       offline machine — leaves onerror unfired and the callback never
       running. */
    setTimeout(function () {
      if (ytState !== 'ready') { ytState = 'failed'; finish(false); }
    }, 6000);
  }

  /* The panel's own wording is the embed refusal. `note` replaces it for the
     one other thing that can be true of a film card — that the film is not up
     yet — and stands the "Watch on YouTube" way out down, because in that
     case there is nowhere for it to go. */
  function fmShowBlocked(on, watchUrl, note) {
    if (!filmView) return;
    filmView.classList.toggle('is-blocked', !!on);
    var link = byId('fmBlockedLink');
    if (link && watchUrl) link.href = watchUrl;
    if (link) link.style.display = note ? 'none' : '';
    if (fmBlockedNote) fmBlockedNote.textContent = note || FM_BLOCKED_REFUSED;
  }

  /* The embed on screen now. The player is built once and every later video
     is loaded into it, so its error handler is bound once too, and it must
     read the video that is playing now, not the one the player was built
     for. Before K22 it held on to the first, and a refused second video sent
     its "Watch on YouTube" to the first one. Harmless with one id on the
     page; wrong the moment a season had ten. */
  var fmSource = null;

  function fmOpenEmbed(source) {
    fmSource = source;
    fmShowBlocked(false, source.watch);
    ytEnsureApi(function (ok) {
      if (fmSource !== source) return;          /* something newer was opened meanwhile */
      if (!ok) { fmShowBlocked(true, source.watch); return; }
      try {
        if (ytPlayer && ytPlayer.loadVideoById) { ytPlayer.loadVideoById(source.id); return; }
        ytPlayer = new window.YT.Player('fmEmbedHost', {
          videoId: source.id,
          playerVars: { autoplay: 1, rel: 0, playsinline: 1, modestbranding: 1 },
          events: {
            onReady: function (e) {
              try { e.target.playVideo(); } catch (err) { /* autoplay may be refused */ }
            },
            /* 101 and 150 are the owner disallowing embedding; 2 and 5 are a
               bad id or an unplayable format. All of them mean the same thing
               to a visitor: not here. */
            onError: function () { fmShowBlocked(true, fmSource && fmSource.watch); }
          }
        });
      } catch (err) { fmShowBlocked(true, source.watch); }
    });
  }

  /* ---- a DWS season's episodes (V2.72 K22) --------------------------------
     An episode plays in this same room: the season view that opened it stays
     behind, and closing the player lands back on it. While a season is
     playing the plate carries its previous and next, and every YouTube video
     carries a plain way out to YouTube itself (Josh: "ideally it also links
     directly to YouTube"), not only the one the refusal panel offers. */
  var fmSeason = null, fmEpisode = 0;

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function playEpisode(season, i) {
    if (!season || !season.episodes || !season.episodes[i]) return;
    var ep = season.episodes[i];
    fmSeason = season;
    fmEpisode = i;
    openFilm({
      kind: 'society',
      kindLabel: 'Dead Warrior Society · Season ' + season.no + ' · Episode ' + pad2(i + 1),
      title: ep[1],
      place: season.place,
      date: ep[2],
      label: 'Dead Warrior Society, Season ' + season.no + ', episode ' + (i + 1) + ': ' + ep[1],
      video: { type: 'embed', id: ep[0], watch: 'https://www.youtube.com/watch?v=' + ep[0] },
      season: season
    });
    try {
      window.dispatchEvent(new CustomEvent('museum:episode', { detail: { season: season, index: i } }));
    } catch (err) { /* an old browser without CustomEvent just misses the mark */ }
  }

  function fmSyncActions(media, source) {
    var bar = byId('fmActions');
    if (!bar) return;
    var prev = byId('fmPrev'), next = byId('fmNext'), watch = byId('fmWatch');
    var inSeason = !!(media && media.season && fmSeason === media.season);
    var embed = !!(source && source.type === 'embed');
    bar.hidden = !(inSeason || embed);
    if (prev) {
      prev.hidden = !inSeason;
      prev.disabled = !inSeason || fmEpisode <= 0;
    }
    if (next) {
      next.hidden = !inSeason;
      next.disabled = !inSeason || fmEpisode >= fmSeason.episodes.length - 1;
    }
    if (watch) {
      watch.hidden = !embed;
      if (embed) watch.href = source.watch;
    }
  }

  /* Where focus was before the player opened, and the way back to it. */
  var fmReturnFocus = null;

  function fmFocusables() {
    return Array.prototype.filter.call(
      filmView.querySelectorAll('button, a[href], video[controls], iframe'),
      function (n) { return !n.disabled && !n.hidden && n.offsetParent !== null; });
  }

  function openFilm(media) {
    if (!filmView || !media) return;
    var source = videoFor(media);
    if (!source) return;

    /* Anything that is not one of a season's episodes lets the season go. */
    if (!media.season) fmSeason = null;
    var wasOpen = filmView.classList.contains('open');
    if (!wasOpen) fmReturnFocus = document.activeElement;
    /* Named for what is playing. It said "Film signal" for everything,
       including the society's recordings. */
    filmView.setAttribute('aria-label', media.label ||
      (media.kind === 'society' ? 'Dead Warrior Society' : 'Film signal'));

    var kinds = (D && D.MEDIA_KINDS) || {};
    byId('fmKind').textContent = media.kindLabel || '';
    byId('fmKind').style.color = (kinds[media.kind] || {}).ink || '';
    byId('fmTitle').textContent = media.title || '';
    byId('fmMeta').textContent =
      [media.place, media.date].filter(Boolean).join(' · ');

    filmView.classList.remove('is-file', 'is-embed', 'is-blocked');
    if (source.type === 'soon') {
      fmShowBlocked(true, null,
        'The film is not up yet. It will play here as soon as it is.');
    } else if (source.type === 'embed') {
      filmView.classList.add('is-embed');
      fmOpenEmbed(source);
    } else {
      filmView.classList.add('is-file');
      if (fmVideo.getAttribute('src') !== source.src) fmVideo.setAttribute('src', source.src);
      /* 51.7 MB streamed by byte range: between the click and the first frame
         there is a real wait on a slow line, and a black panel is not an
         answer to it. Cleared by the video's own events, wired once below. */
      if (fmVideo.readyState < 3) loadNote('film', 'Loading the film…');
      var play = fmVideo.play();
      /* Autoplay with sound is refused by every browser unless the visitor
         has already interacted — they have, by clicking, but a refusal is not
         an error worth surfacing. The controls are right there. */
      if (play && play.catch) play.catch(function () {});
    }

    fmSyncActions(media, source);
    filmView.classList.add('open');
    document.body.classList.add('film-open');
    /* Focus goes into the room and stays in it (Tab is trapped below), and
       goes back to whatever opened it on close. The player had none of this:
       focus stayed on the card behind a screen-sized overlay. */
    if (!wasOpen) {
      var close = byId('fmClose');
      if (close) setTimeout(function () { close.focus({ preventScroll: true }); }, 60);
    }
  }

  function closeFilm() {
    if (!filmView || !filmView.classList.contains('open')) return;
    filmView.classList.remove('open');
    document.body.classList.remove('film-open');
    fmSource = null;
    fmSeason = null;
    var back = fmReturnFocus;
    fmReturnFocus = null;
    setTimeout(function () {
      if (back && back.focus && back !== document.body && document.documentElement.contains(back)) {
        back.focus({ preventScroll: true });
      }
    }, 0);
    loadNoteDone('film');
    /* Paused and wound back, not just hidden: a hidden video carries on
       playing, and its sound with it. */
    try { fmVideo.pause(); fmVideo.currentTime = 0; } catch (err) { /* not fatal */ }
    /* The embed is stopped rather than torn down: the player is expensive to
       build and stopVideo leaves it ready for the next one. */
    if (ytPlayer && ytPlayer.stopVideo) {
      try { ytPlayer.stopVideo(); } catch (err) { /* not fatal */ }
    }
    filmView.classList.remove('is-file', 'is-embed', 'is-blocked');
  }

  var filmWired = false;

  function filmWire() {
    if (filmWired) return;
    filmView = byId('filmView');
    if (!filmView) return;
    filmWired = true;
    fmVideo = byId('fmVideo');
    fmBlockedNote = filmView.querySelector('#fmBlocked p');
    FM_BLOCKED_REFUSED = fmBlockedNote ? fmBlockedNote.textContent : '';

    /* Wired once rather than per open, so nothing accumulates on the element.
       Every one of these means the wait is over, including the failures: the
       browser's own error panel is the report then, not a status line. */
    ['loadeddata', 'canplay', 'playing', 'error', 'emptied'].forEach(function (ev) {
      if (fmVideo) fmVideo.addEventListener(ev, function () { loadNoteDone('film'); });
    });
    var close = byId('fmClose');
    if (close) close.addEventListener('click', closeFilm);
    /* The backdrop closes; the player and its controls do not. */
    filmView.addEventListener('click', function (e) {
      if (e.target === filmView || e.target.classList.contains('fm-inner')) closeFilm();
    });
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && filmView.classList.contains('open')) closeFilm();
    });

    /* A season's previous and next (V2.72 K22). */
    var prev = byId('fmPrev'), next = byId('fmNext');
    if (prev) prev.addEventListener('click', function () {
      if (fmSeason && fmEpisode > 0) playEpisode(fmSeason, fmEpisode - 1);
    });
    if (next) next.addEventListener('click', function () {
      if (fmSeason && fmEpisode < fmSeason.episodes.length - 1) playEpisode(fmSeason, fmEpisode + 1);
    });

    /* Tab stays in the room. The YouTube frame keeps its own keys, since it
       is another origin and a keydown in it never reaches this page, so the
       two guards at either end of the room catch focus leaving it that way
       and hand it round to the other end. */
    function wrap(toEnd) {
      var list = fmFocusables().filter(function (n) { return !n.classList.contains('fm-guard'); });
      if (!list.length) return;
      (toEnd ? list[list.length - 1] : list[0]).focus({ preventScroll: true });
    }
    filmView.querySelectorAll('.fm-guard').forEach(function (g) {
      g.addEventListener('focus', function () { wrap(g.getAttribute('data-edge') === 'start'); });
    });
    filmView.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !filmView.classList.contains('open')) return;
      var list = fmFocusables().filter(function (n) { return !n.classList.contains('fm-guard'); });
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ======================================================================
     PART C — THE FIELD ATLAS
     Each region marker is a folder. Opening one unfolds its cities as
     sub-pins on the chart and slides the atlas out from the left, the two
     halves of one view: the panel lists what is on the map, the map shows
     where the panel's entries were taken.
     ====================================================================== */

  /* ---- filter state -----------------------------------------------------
     Two facets, one rule (V2.72 K14). Place and media are sibling rows of
     the same control: each holds one choice or none ("All"), each narrows
     the list, and pressing the live chip again lets it go. Before this the
     region was a scope — a dismissible chip only a marker or the rail could
     set — and the kinds were a multi-select beside it: they looked alike and
     behaved nothing alike. */
  var atlasFilters = { region: null, kind: null };

  function atlasPasses(m) {
    if (atlasFilters.region !== null && m.region !== atlasFilters.region) return false;
    if (atlasFilters.kind !== null && m.kind !== atlasFilters.kind) return false;
    return true;
  }

  /* ---- the panel -------------------------------------------------------- */
  var atlasPanel = null, atlasGrid = null, atlasCount = null;
  var placeRow = null, mediaRow = null;
  var placeChips = [], mediaChips = [];

  function atlasScroller() {
    return atlasPanel ? atlasPanel.querySelector('.ap-scroll') : null;
  }

  /* The chips are built once and updated in place rather than re-rendered: a
     row rebuilt under a keyboard user's focus throws that focus away. */
  function addChip(row, list, value, label, iconHtml) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ap-chip';
    b.innerHTML = (iconHtml || '') + '<span>' + label + '</span><span class="n"></span>';
    row.appendChild(b);
    list.push({ el: b, value: value, label: label, n: b.querySelector('.n') });
  }

  function buildChips() {
    var REGIONS = D.REGIONS, KINDS = D.MEDIA_KINDS, ICON = D.ICON_SVG;
    placeRow.innerHTML = '';
    mediaRow.innerHTML = '';
    placeChips = [];
    mediaChips = [];
    addChip(placeRow, placeChips, null, 'All');
    /* Journey order, the order the stepper reads in. */
    D.REGION_TAB_ORDER.forEach(function (i) { addChip(placeRow, placeChips, i, REGIONS[i].name); });
    addChip(mediaRow, mediaChips, null, 'All');
    /* A kind with nothing in the catalogue gets no chip, rather than one that
       sits disabled at 0 for good. With fewer than two kinds left there is
       nothing to choose between, so the whole MEDIA row steps out. Both come
       back on their own as soon as a second kind has an entry (V2.72 K21). */
    var kinds = Object.keys(KINDS).filter(function (k) {
      return D.MEDIA_FLAT.some(function (m) { return m.kind === k; });
    });
    kinds.forEach(function (k) {
      /* The kind's own ink on its icon, the same ink its cards carry. */
      addChip(mediaRow, mediaChips, k, KINDS[k].chip || KINDS[k].label,
              '<span class="ink" style="color:' + KINDS[k].ink + '">' + ICON[k] + '</span>');
    });
    var mediaFacet = mediaRow.parentNode;
    if (mediaFacet && mediaFacet.classList.contains('ap-facet')) mediaFacet.hidden = kinds.length < 2;
    placeRow.addEventListener('click', function (e) {
      var c = pickChip(placeChips, e);
      if (c) setRegion(c.value === atlasFilters.region ? null : c.value);
    });
    mediaRow.addEventListener('click', function (e) {
      var c = pickChip(mediaChips, e);
      if (c) setKind(c.value === atlasFilters.kind ? null : c.value);
    });
  }

  function pickChip(list, e) {
    var el = e.target.closest ? e.target.closest('.ap-chip') : null;
    if (!el || el.disabled) return null;
    for (var i = 0; i < list.length; i++) if (list[i].el === el) return list[i];
    return null;
  }

  /* Each row is counted under the other row's choice, so a number always says
     what pressing it would show — and a chip that would show nothing is
     disabled rather than left to be discovered as an empty list. */
  function atlasRenderChips() {
    var FLAT = D.MEDIA_FLAT, KINDS = D.MEDIA_KINDS;
    var underKind = FLAT.filter(function (m) {
      return atlasFilters.kind === null || m.kind === atlasFilters.kind;
    });
    var underRegion = FLAT.filter(function (m) {
      return atlasFilters.region === null || m.region === atlasFilters.region;
    });
    placeChips.forEach(function (c) {
      var n = c.value === null ? underKind.length
        : underKind.filter(function (m) { return m.region === c.value; }).length;
      paintChip(c, n, atlasFilters.region === c.value,
                (c.value === null ? 'All places' : c.label) + ', ' + entriesLabel(n));
    });
    mediaChips.forEach(function (c) {
      var n = c.value === null ? underRegion.length
        : underRegion.filter(function (m) { return m.kind === c.value; }).length;
      paintChip(c, n, atlasFilters.kind === c.value,
                (c.value === null ? 'All media' : KINDS[c.value].label) + ', ' + entriesLabel(n));
    });
  }

  function paintChip(c, n, on, aria) {
    c.n.textContent = String(n);
    c.el.setAttribute('aria-pressed', on ? 'true' : 'false');
    c.el.setAttribute('aria-label', aria);
    c.el.disabled = (n === 0 && !on);
  }

  /* On a phone the rows scroll sideways, so the live chip can be off the end
     of its row — Japan is, whenever a marker or the stepper chooses it. Bring
     it in, clear of the fade. Nothing moves on a desktop, where rows wrap. */
  function revealLiveChips() {
    [[placeRow, placeChips], [mediaRow, mediaChips]].forEach(function (pair) {
      var row = pair[0];
      if (!row || row.scrollWidth <= row.clientWidth) return;
      var live = null;
      pair[1].forEach(function (c) { if (c.el.getAttribute('aria-pressed') === 'true') live = c.el; });
      if (!live) return;
      var r = row.getBoundingClientRect(), b = live.getBoundingClientRect();
      var FADE = 28, EDGE = 18;
      var dx = b.right > r.right - FADE ? b.right - (r.right - FADE)
             : b.left < r.left + EDGE ? b.left - (r.left + EDGE) : 0;
      if (!dx) return;
      if (row.scrollTo) row.scrollTo({ left: row.scrollLeft + dx, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      else row.scrollLeft += dx;
    });
  }

  /* The map follows the place filter: a country frames that country, All
     frames the whole chart. The media filter moves nothing — it thins the
     list, and folds away any city it has emptied. */
  function setRegion(idx) {
    if (idx === atlasFilters.region) return;
    atlasFilters.region = idx;
    atlasRefresh();
    if (idx === null) chart.home(900);
    else chart.focusRegion(idx, 1200);
  }

  function setKind(k) {
    if (k === atlasFilters.kind) return;
    atlasFilters.kind = k;
    atlasRefresh();
  }

  function atlasRenderCards() {
    var MEDIA_FLAT = D.MEDIA_FLAT, MEDIA_KINDS = D.MEDIA_KINDS;
    var STOPS = D.STOPS, PLACE_LABEL = D.PLACE_LABEL, ICON = D.ICON_SVG;
    var shown = MEDIA_FLAT.filter(atlasPasses);

    /* The head says what the filters found: the entries, and the places they
       were taken in. */
    var places = {};
    shown.forEach(function (m) { places[m.cityKey] = true; });
    var nPlaces = Object.keys(places).length;
    if (atlasCount) {
      atlasCount.textContent = entriesLabel(shown.length) + ' · ' +
        nPlaces + (nPlaces === 1 ? ' place' : ' places');
    }

    /* Unreachable from the chips — a chip that would empty the list is
       disabled — but kept as the way out for any other route in. */
    if (!shown.length) {
      atlasGrid.innerHTML =
        '<div class="ap-empty">Nothing in the atlas matches those filters.' +
        '<div><button type="button" class="ap-clear" id="apClearEmpty">Clear filters</button></div></div>';
      var e = byId('apClearEmpty');
      if (e) e.addEventListener('click', atlasClearFilters);
      return;
    }

    /* One card at both widths: the thumbnail, the kind in its own ink, the
       title in full, then where and when. */
    function card(m) {
      var kind = MEDIA_KINDS[m.kind];
      /* A season's kind line carries its number, and its second line its
         episodes where a photograph's carries a date. */
      var where = m.place || PLACE_LABEL[m.stop] || (STOPS[m.stop] || {}).name || '';
      return '<button type="button" class="ap-card' + (m.season ? ' ap-card--season' : '') +
        '" data-key="' + m.key + '" data-city="' + m.cityKey + '">' +
        '<img class="ap-thumb" src="' + D.thumbFor(m) + '" alt="" width="76" height="54" loading="lazy" decoding="async">' +
        '<span class="ap-card-text">' +
          '<span class="ap-kind" style="color:' + kind.ink + '">' + (m.kindLabel || kind.label) + '</span>' +
          '<span class="ap-name">' + m.title + '</span>' +
          '<span class="ap-meta">' + ICON.pin +
            '<span>' + [where, m.date].filter(Boolean).join(' · ') + '</span></span>' +
        '</span>' +
      '</button>';
    }

    /* Grouped by place and numbered by place, so a place keeps its number
       however the list is filtered: the journey's places in the order Josh
       set (01–04 since V3.0), then the Dead Warrior Society's places in season
       order (05–10). A season is a place's entry like a Photo Cache is, so
       DWS lists place by place too (Josh, K24: "we should still be navigating
       location based"). The country belongs to the place row; the head names
       the place, and each card says the rest. */
    var html = [];
    STOPS.forEach(function (stop, r) {
      var items = shown.filter(function (m) { return m.stop === r; });
      if (!items.length) return;
      html.push('<div class="ap-group-head">' +
        '<span class="ap-group-num">' + String(r + 1).padStart(2, '0') + '</span>' +
        '<span class="ap-group-name">' + stop.name + '</span>' +
        '<span class="ap-group-rule" aria-hidden="true"></span>' +
        '<span class="ap-group-count">' + entriesLabel(items.length) + '</span></div>');
      html.push(items.map(card).join(''));
    });
    atlasGrid.innerHTML = html.join('');

    atlasGrid.querySelectorAll('.ap-card').forEach(function (el) {
      var key = el.dataset.key;
      /* Hovering a card lights its place on the chart, and hovering the place
         lights the card: the link has to read in both directions or the
         panel and the map look like two unrelated lists. The host lights the
         place's own pin if it is out, or the marker it stands under. */
      var cityKey = el.dataset.city;
      el.addEventListener('pointerenter', function () {
        atlasCardHoverKey = cityKey;
        chart.highlightSub(cityKey);
      });
      el.addEventListener('pointerleave', function () {
        if (atlasCardHoverKey === cityKey) {
          atlasCardHoverKey = null;
          chart.highlightSub(null);
        }
      });
      el.addEventListener('click', function () {
        var media = MEDIA_FLAT.filter(function (m) { return m.key === key; })[0];
        /* A season opens as a season: its cover and every episode, to pick
           from. The host owns that view. */
        if (media && media.season) {
          if (onSeason) onSeason(media.season, el);
          else playEpisode(media.season, 0);
          return;
        }
        /* Moving pictures are not prints on a wall. They have no place in a
           room built to hang photographs, so anything with a video behind it
           opens in its own player and the museum is left out of it. */
        if (media && videoFor(media)) { openFilm(media); return; }
        /* A photo card used to fly the camera at the pin and walk into the
           3D room. There is no room and no dive; the host says what a
           photograph opens. */
        if (onEnterStop) onEnterStop(parseInt(key.split(':')[0], 10), key);
      });
    });
  }

  /* Called from the chart when the pointer moves over a sub-pin. The sub-pin
     is a city and a card is one entry inside it, so pointing at a sub-pin
     lights every card filed under that city rather than one of them. The
     chart and the list are two views of one set, so pointing at either lights
     the other. Pointing at a sub-pin also brings its cards into view: the
     list is long enough that the highlight was often happening somewhere
     nobody could see. */
  var atlasScrolledToKey = null;
  var atlasCardHoverKey = null;

  /* A key, or several: a marker holding two places lights both (K24). */
  function atlasLinkCard(key) {
    if (!atlasGrid) return;
    var keys = key ? [].concat(key) : [];
    key = keys.length ? keys.join('|') : null;
    var first = null;
    atlasGrid.querySelectorAll('.ap-card').forEach(function (el) {
      var on = keys.indexOf(el.dataset.city) !== -1;
      el.classList.toggle('linked', on);
      if (on && !first) first = el;
    });
    if (!key) { atlasScrolledToKey = null; return; }
    /* Only once per arrival at a sub-pin. Re-running it on every pointer
       move would fight the visitor's own scrolling for as long as they
       hovered. */
    if (first && atlasScrolledToKey !== key) {
      atlasScrolledToKey = key;
      if (!atlasCardInView(first)) {
        first.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'center'
        });
      }
    }
  }

  /* Scrolling something already on screen is motion for nothing. */
  function atlasCardInView(el) {
    var box = el.getBoundingClientRect();
    var host = atlasGrid.parentElement || atlasGrid;
    var view = host.getBoundingClientRect();
    return box.top >= view.top && box.bottom <= view.bottom;
  }

  /* atlasSyncHotspots, without a scene to sync: which places are due out,
     which markers still have something in them, and whether a close-up is
     wanted. What used to be five ThreeJS groups easing their `target` is now
     one list handed to the chart, and the set of markers that have handed
     over to their places is carried in each place's `parent`.
     V2.72 K24: a marker is a place (or two close ones), not a country, so a
     chosen country hands over every one of its markers, and a place is out
     when it holds something that passes the filters. Under a media filter a
     marker with nothing of that kind leaves the chart while the atlas is in
     use, so the map shows what the list shows. Before this the US marker
     stood in the Midwest under US + DWS, holding nothing, with its two
     seasons as seals on the coast (Josh: "once u filter to dws it breaks"). */
  function atlasSyncHotspots() {
    var STOPS = D.STOPS, MEDIA_FLAT = D.MEDIA_FLAT, STATIONS = D.STATIONS || [];
    var SHEETS = D.REGION_SHEET || (D.sheets && D.sheets.regions) || {};
    /* Unfolded whenever a region is chosen or the panel is up — the selection
       belongs to the chart, not to whether the list happens to be showing. */
    var live = atlasPanel.classList.contains('open') || atlasFilters.region !== null;
    var ofKind = function (m) { return atlasFilters.kind === null || m.kind === atlasFilters.kind; };
    var list = [];
    STOPS.forEach(function (city, r) {
      /* A place belongs to its country and comes out only when that country
         has been chosen. The panel being open is not enough on its own: with
         no country selected it would unfold everywhere at once, which is a
         list of everywhere rather than a look at somewhere. */
      var inScope = city.country === atlasFilters.region;
      /* ...and only if something in it survives the filters: a place whose
         only entries have been filtered out has nothing to open. */
      var hasAny = MEDIA_FLAT.some(function (m) { return m.stop === r && atlasPasses(m); });
      if (!(live && inScope && hasAny)) return;
      list.push({
        key: 'city:' + r,
        parent: D.STOP_STATION[r],
        uv: (city.chartUv || []).slice(),
        angle: city.angle || 0,
        /* A city is a place, not a category, so every sub-pin reads the same:
           in 3D it took the chart's own pin colour (#f2ecdd) rather than one
           of the three category inks, which stay with the catalogue where
           they are doing the work of telling photo from film from note. One
           constant kind keeps that true through a facade that wants one. */
        kind: 'photo',
        label: city.name
      });
    });
    chart.setSubs(list);
    chart.showStations(STATIONS.map(function (st) {
      return !live || st.stops.some(function (r) {
        return MEDIA_FLAT.some(function (m) { return m.stop === r && ofKind(m); });
      });
    }));
    /* Regions with a sheet of their own dissolve to it; the rest keep the
       world. None has one since K24 (see REGION_SHEET in museum-data.js). */
    var wantRegion = (live && atlasFilters.region !== null &&
                      SHEETS[atlasFilters.region]) ? atlasFilters.region : null;
    chart.setRegionSheet(wantRegion);
  }

  function atlasClearFilters() {
    atlasFilters.kind = null;
    if (atlasFilters.region !== null) setRegion(null);
    else atlasRefresh();
  }

  function atlasRefresh() {
    if (!mounted) return;
    atlasRenderChips();
    revealLiveChips();
    atlasRenderCards();
    var sc = atlasScroller();
    if (sc) sc.scrollTop = 0;
    atlasSyncHotspots();
  }

  /* ---- opening and closing ----------------------------------------------
     The panel covers the left of the screen, so the chart is pushed into the
     remaining strip. In 3D that was a projection offset — the lighting and
     the fold stayed exactly as composed; here it is the chart re-centring
     itself around the width we hand it. Below 860px the atlas is a bottom
     sheet, not a side panel. */
  function atlasIsSheet() { return window.matchMedia('(max-width: 860px)').matches; }

  var atlasPanelW = 0;

  function atlasSetPanelWidth(open) {
    /* As a sheet it takes the full width, and positioning the chart against
       that width would throw it off the screen. */
    var w = (open && !atlasIsSheet()) ? atlasPanel.getBoundingClientRect().width : 0;
    /* A layout engine is not guaranteed to have measured the panel yet; fall
       back to what the stylesheet asks for so the offset is never zero while
       the panel is up. Kept in step with --atlas-panel-w (380px) in the
       stylesheet by hand. */
    if (open && !atlasIsSheet() && !w) {
      var stage = byId('stage');
      w = Math.min(380, stage ? stage.clientWidth : window.innerWidth);
    }
    atlasPanelW = w;
    document.documentElement.style.setProperty('--panel-w', w + 'px');
  }

  /* atlasApplyShift, as two facade calls. The 3D page eased one scalar,
     `atlasShift`, at 0.10 per frame (≈0.98s to settle) and fed it to
     camera.setViewOffset — sideways for the side panel, upward for the sheet.
     The facade takes the destination rather than the eased value, so the
     easing belongs to the chart now: full panel width on a desktop, the peek
     fraction on a phone, and zero for whichever axis is not in play. */
  function atlasApplyShift() {
    var open = atlasPanel.classList.contains('open');
    var sheet = atlasIsSheet();
    chart.setPanelWidth(open && !sheet ? atlasPanelW : 0);
    /* 0.7 at the peek stop, 1 at full — the chart only needs lifting by as
       much as the sheet actually covers. It was 0.55 before K14 raised the
       peek. The lift does two jobs that pull against each other: a focused
       region lands on the lifted centre, while at home the markers — all in
       the northern third of the chart — sit well above it. On a 390 × 844
       phone 0.7 puts a focused region at 239px and, at home, Italy's marker at
       about 103px, both inside the strip between the pills and the sheet. */
    chart.setPeek(open && sheet ? (sheetState === 'full' ? 1 : 0.7) : 0);
  }

  /* Where focus came from, so it can be put back. A panel that steals focus
     and never returns it strands anyone not using a pointer. */
  var atlasReturnFocus = null;

  /* Escape closes the panel. It did nothing at all before — the panel carried
     role="dialog", toggled aria-hidden, took focus on open and gave it back
     on close, and then ignored the one key everybody presses. Bound only
     while the panel is up.

     Bound in the CAPTURE phase, which is the whole trick. The film's own
     Escape handler is on `window` too, and it was registered first (at
     mount), so on the bubble phase it runs first, closes the film, and by the
     time this one is asked "is the film open?" the answer is already no — one
     press would take down the film and the panel behind it. Capturing puts
     this first in the path, so it can stand down while something is in front
     of it and leave that thing's own handler to do the work. */
  function atlasKeydown(e) {
    if (e.key !== 'Escape') return;
    if (!atlasPanel.classList.contains('open')) return;
    /* The film is in front of the panel and has its own Escape; The Hang is
       in front of both. Neither is ours to close. */
    if (filmView && filmView.classList.contains('open')) return;
    if (hangIsOpen()) return;
    /* A DWS season is in front of the panel too, with its own Escape. */
    if (document.body.classList.contains('season-open')) return;
    closeAtlas();
  }

  /* The Hang marks itself open. It is asked for two ways because it answers
     two ways: museum-hang.js puts `hang-open` on its own host
     (museum-hang.js:523, styled `.hang-host.hang-open`), while this module and
     museum-chart.js's keyboard both read `document.body`. Until one of them
     wins, test both — a guard that is looking in the wrong place is the same
     as no guard, and the cost of being wrong here is Escape taking the panel
     down behind a wall of photographs. */
  function hangIsOpen() {
    if (document.body.classList.contains('hang-open')) return true;
    return !!document.querySelector('.hang-open');
  }

  /* V2.72 (Josh): while the atlas is open the page's return control means the
     map, not the store. The corner pill reads its map label and a plain click
     closes the atlas (a modified click still opens the store in a new tab). On
     desktop the drawer's own back control does the job and the pill steps
     aside (museum.css); on a phone the pill above the sheet is the one. */
  function syncReturn(open) {
    var pill = byId('returnSite');
    if (!pill) return;
    var label = pill.querySelector('[data-return-label]');
    var text = pill.getAttribute(open ? 'data-map-label' : 'data-site-label');
    if (label && text) label.textContent = text;
    pill.classList.toggle('is-map', !!open);
  }

  function openAtlasPanel() {
    if (!atlasPanel.classList.contains('open')) {
      atlasReturnFocus = document.activeElement;
    }
    atlasPanel.classList.add('open');
    atlasPanel.setAttribute('aria-hidden', 'false');
    syncReturn(true);
    atlasSetPanelWidth(true);
    var tooltip = byId('tooltip');
    if (tooltip) tooltip.style.display = 'none';
    if (atlasIsSheet()) atlasSetSheet('peek'); else atlasApplyShift();
    window.addEventListener('keydown', atlasKeydown, true);
    var close = byId('apClose');
    if (close) setTimeout(function () { close.focus({ preventScroll: true }); }, 90);
  }

  /* Everything, unscoped. The map is a way of finding things, not the only
     way in — without this the catalogue is invisible until a region is
     guessed at. */
  function openAtlasAll() {
    atlasFilters.region = null;
    openAtlasPanel();
    atlasRefresh();
    /* MAP_HOME.radius * 0.86 was 7.4 / 6.364 = 1.163x home. */
    chart.zoomHome(1.163, 900);
  }

  /* Let go of the region without closing anything: the place row goes back to
     All, the sub-pins fold away and every folder comes back. */
  function clearRegion() {
    atlasFilters.region = null;
    atlasRefresh();
    /* Always back to the whole chart, panel open or not. This used to be
       skipped while the panel was up — which is exactly when the chip's X is
       reachable, so dismissing a country left the chart parked on it with
       nothing selected and the other two markers out of frame. The panel's
       own offset keeps the sheet centred in whatever width is left, so home
       is the right target either way. */
    chart.home(900);
  }

  function openRegion(idx) {
    atlasFilters.region = idx;
    /* Arriving at a place from the map or the stepper is a request for that
       place. A media filter that would leave it empty lets go, rather than
       opening the panel on nothing. */
    if (atlasFilters.kind !== null && !D.MEDIA_FLAT.some(atlasPasses)) atlasFilters.kind = null;
    openAtlasPanel();
    atlasRefresh();
    /* Ease in over the region rather than diving at it — the dive belonged to
       opening a piece of media, and reusing it here would say the wrong
       thing. Once a region has a sheet of its own its marker is standing on a
       stretch of somewhere else; frame where the region's contents will
       actually be instead. */
    chart.focusRegion(idx, 1200);
  }

  function closeAtlas() {
    if (!atlasPanel.classList.contains('open')) return;
    /* Closing the list lets go of the country as well. The selection existed
       to scope the list; with the list gone it was only still showing as a lit
       set of city sub-pins over a chart nobody had asked to be in. */
    atlasFilters.region = null;
    atlasPanel.classList.remove('open');
    atlasPanel.setAttribute('aria-hidden', 'true');
    syncReturn(false);
    atlasPanel.style.transform = '';
    atlasSetSheet('peek');      /* also clears the height classes off the sheet */
    atlasSetPanelWidth(false);
    window.removeEventListener('keydown', atlasKeydown, true);
    /* Handing focus back only works if the thing that opened the panel can
       take it. Opened from the chart, that was the body — and focusing the
       body does nothing, so focus stayed on a close button that had just been
       hidden. Check that it actually landed, and fall back to the way back in:
       the Field Atlas pill, since the nav that also carries one is hidden on
       this page. */
    var wasRegion = atlasFilters.region;
    var target = atlasReturnFocus;
    atlasReturnFocus = null;
    var fallback = byId('atlasOpen') || byId('navAtlas');
    setTimeout(function () {
      if (target && target.focus && target !== document.body) {
        target.focus({ preventScroll: true });
      }
      var live = document.activeElement;
      if (!live || live === document.body || atlasPanel.contains(live)) {
        if (fallback) fallback.focus({ preventScroll: true });
      }
    }, 0);
    atlasApplyShift();
    atlasSyncHotspots();
    atlasCardHoverKey = null;
    chart.highlightSub(null);
    /* Pull back enough to see the region in context, rather than all the way
       home, which would throw away the position as well as the selection.
       Unreachable as shipped: `wasRegion` is read after the region has been
       let go above, so it is always null. Kept for the day the region
       survives a close. 7.4 / (7.4 * 0.72) = 1.389x home. */
    if (wasRegion === null) chart.home(1100);
    else chart.zoomHome(1.389, 1100);
  }

  /* ---- the bottom sheet -------------------------------------------------
     The sheet has two heights. Opening at full height buried the chart;
     opening low enough to see the chart left almost no room for cards. Two
     stops with a drag between them lets the same gesture do both, which is
     how every native map sheet behaves. */
  /* Fraction of the viewport the sheet is lowered by at its first stop. 0.40
     until K14; the sheet now carries two filter rows above the record, and at
     0.40 that left room for one card. 0.30 is M1's sheet: about 520px of
     atlas under about 320px of chart. */
  var SHEET_PEEK = 0.30;
  var sheetState = 'peek';

  function atlasSetSheet(state) {
    sheetState = state;
    atlasPanel.classList.toggle('peek', state === 'peek');
    atlasPanel.classList.toggle('full', state === 'full');
    atlasPanel.style.setProperty('--sheet-y', state === 'full' ? '0dvh' : (SHEET_PEEK * 100) + 'dvh');
    if (state === 'peek') {
      var sc = atlasScroller();
      if (sc) sc.scrollTop = 0;   /* collapsing with the list scrolled hides its start */
    }
    if (atlasPanel.classList.contains('open')) atlasApplyShift();
  }

  function atlasSheetDrag() {
    var grab = byId('apGrab');
    var head = atlasPanel.querySelector('.ap-head');
    var scroller = atlasScroller();
    if (!scroller) return;
    var startY = 0, dy = 0, dragging = false, base = 0, moved = false, fromList = false;
    var suppressClick = false;

    function baseY() { return sheetState === 'full' ? 0 : SHEET_PEEK * window.innerHeight; }

    /* Dragging the list raises the sheet at the peek stop, and lowers it
       again from the top of a full one. In between, the list just scrolls. */
    function listCanDrag() { return sheetState === 'peek' || scroller.scrollTop <= 0; }

    function down(e) {
      if (!atlasIsSheet()) return;
      fromList = e.currentTarget === scroller;
      /* In the header the buttons are controls, so a press on one is not a
         drag. In the list almost everything is a card, so the same rule would
         mean the gesture only worked in the gaps between them. */
      if (!fromList && e.target.closest('button')) return;
      if (fromList && !listCanDrag()) return;
      dragging = true; moved = false; startY = e.clientY; dy = 0; base = baseY();
      atlasPanel.classList.add('dragging');
      if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    }
    function move(e) {
      if (!dragging) return;
      dy = e.clientY - startY;
      /* At full height a pull upward from the top of the list is a scroll,
         not a drag — hand it back rather than rubber-banding a sheet that has
         nowhere left to go. */
      if (fromList && sheetState === 'full' && dy < 0) {
        dragging = false;
        atlasPanel.classList.remove('dragging');
        atlasPanel.style.transform = '';
        return;
      }
      if (Math.abs(dy) > 4) moved = true;
      atlasPanel.style.transform = 'translateY(' + Math.max(0, base + dy) + 'px)';
    }
    function up() {
      if (!dragging) return;
      dragging = false;
      atlasPanel.classList.remove('dragging');
      atlasPanel.style.transform = '';
      /* A drag that began on a card must not also open that card. */
      suppressClick = moved && fromList;
      if (!moved) {                                   /* a tap toggles the height */
        if (!fromList) atlasSetSheet(sheetState === 'full' ? 'peek' : 'full');
        return;                                       /* a tap on a card is not a toggle */
      }
      if (dy < -60) atlasSetSheet('full');
      else if (dy > 90) {
        if (sheetState === 'full') atlasSetSheet('peek');
        else closeAtlas();
      }
    }
    scroller.addEventListener('click', function (e) {
      if (!suppressClick) return;
      suppressClick = false;
      e.stopPropagation();
      e.preventDefault();
    }, true);

    /* A wheel over the list does the same thing a swipe does, for a narrow
       window on a laptop. */
    scroller.addEventListener('wheel', function (e) {
      if (!atlasIsSheet() || sheetState !== 'peek') return;
      if (e.deltaY > 0) { e.preventDefault(); atlasSetSheet('full'); }
    }, { passive: false });

    [grab, head, scroller].forEach(function (el) {
      if (!el) return;
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
    });
  }

  /* The panel hangs below the nav, whatever the nav happens to measure. */
  function atlasFitNav() {
    var nav = byId('siteNav');
    /* #siteNav is display:none unconditionally (museum.css.liquid:871), so
       offsetHeight is 0 and --nav-h lands on 0px — which is what the panel
       wants, since with no visible nav it should run the full height of the
       stage. It is right by accident, and the accident is load-bearing: the
       `nav ? … : 64` test below guards a *missing* element, not a hidden one,
       so a measured 0 is passed straight through on purpose. Substituting 64
       for it would push the panel down past a nav nobody can see. If the nav
       is ever unhidden this starts being right on purpose instead. */
    var h = nav ? nav.offsetHeight : 64;
    document.documentElement.style.setProperty('--nav-h', h + 'px');
  }

  /* ---- mount ------------------------------------------------------------ */
  function mount(opts) {
    opts = opts || {};
    /* Called again with a new facade — a section reload, a chart rebuilt —
       take the new one and leave the DOM and the listeners alone. */
    if (mounted) {
      if (opts.data) D = opts.data;
      if (opts.chart) chart = makeFacade(opts.chart);
      if (opts.onEnterStop) onEnterStop = opts.onEnterStop;
      if (opts.onSeason) onSeason = opts.onSeason;
      atlasRefresh();
      return;
    }

    D = opts.data || window.MUSEUM_DATA;
    chart = makeFacade(opts.chart);
    onEnterStop = typeof opts.onEnterStop === 'function' ? opts.onEnterStop : null;
    onSeason = typeof opts.onSeason === 'function' ? opts.onSeason : null;
    if (!D) return;

    atlasPanel = byId('atlasPanel');
    atlasGrid = byId('apGrid');
    atlasCount = byId('apCount');
    placeRow = byId('apPlaces');
    mediaRow = byId('apMedia');

    filmBuildSources();
    filmWire();
    if (!atlasPanel || !atlasGrid || !placeRow || !mediaRow) return;

    buildChips();

    var close = byId('apClose');
    if (close) close.addEventListener('click', closeAtlas);
    var back = byId('apReturn');
    if (back) back.addEventListener('click', closeAtlas);
    var pill = byId('returnSite');
    if (pill) pill.addEventListener('click', function (e) {
      if (!atlasPanel.classList.contains('open')) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      closeAtlas();
    });
    syncReturn(false);
    atlasSheetDrag();

    window.addEventListener('resize', function () {
      atlasFitNav();
      var open = atlasPanel.classList.contains('open');
      if (open && atlasIsSheet()) atlasSetSheet(sheetState);
      /* Re-measured either way, and on a sheet that means writing 0 — a window
         dragged from desktop width down to sheet width would otherwise leave
         --panel-w holding the old side-panel measurement. */
      atlasSetPanelWidth(open);
      atlasApplyShift();
    });

    mounted = true;
    atlasFitNav();
    atlasSetPanelWidth(false);
    atlasApplyShift();
    atlasRefresh();
  }

  window.FORUM_ATLAS = {
    mount: mount,
    /* A number scopes the panel to that region; null opens it on everything. */
    open: function (regionIndex) {
      if (!mounted) return;
      if (regionIndex === null || regionIndex === undefined) openAtlasAll();
      else openRegion(regionIndex);
    },
    close: function () { if (mounted) closeAtlas(); },
    isOpen: function () { return !!(atlasPanel && atlasPanel.classList.contains('open')); },
    refresh: atlasRefresh,
    /* The chart hovered a place's pin or a marker, or left one. */
    linkCity: function (cityKey) { atlasLinkCard(cityKey || null); },
    cardHoverCity: function () { return atlasCardHoverKey; },
    /* Let go of the region and go home without closing the panel — a press on
       blank paper. */
    clearRegion: function () { if (mounted) clearRegion(); },
    region: function () { return atlasFilters.region; },
    /* A DWS season's episode, in the player (V2.72 K22). */
    playEpisode: function (season, index) { playEpisode(season, index || 0); },
    version: '1'
  };
})();
